import type { TrainingPlanDto, TrainingPlanSummaryDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime, formatTime } from '../../lib/format';
import { draftFromPlan, draftToInput, emptyDraft, sessionMinutes, TRAINING_PLANS_RESOURCE, type PlanDraft } from '../../lib/training';
import { Button, Dialog, EmptyState, ErrorBlock, Field, Input, LoadingBlock, useToast } from '../ui';
import { TestBadge } from './common';
import { OfflineSaveButton } from './OfflineSaveButton';
import { PlanEditor } from './PlanEditor';
import { PlanPrintSheet, PRINT_BODY_CLASS } from './PlanPrintSheet';
import { PlanView } from './PlanView';
import { PlanImageButton } from './ShareImageButton';

/**
 * Abschnitt "Einheit" eines Trainings: die geplante Einheit ansehen, planen,
 * aus einer Vorlage uebernehmen, als Vorlage sichern, drucken, loeschen.
 *
 * Gelesen werden darf mit Leserecht auf die Trainingsplanung, geschrieben
 * (Knoepfe sichtbar) nur mit "Bearbeiten".
 */

export interface PlanSession {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  location?: string | null;
}

type Panel = 'plan' | 'templates' | 'saveTemplate';

function printSubtitle(session: PlanSession, teamName: string | null | undefined): string {
  return [teamName, `${formatDateTime(session.start_at)}–${formatTime(session.end_at)}`, session.location || null].filter(Boolean).join(' · ');
}

export function SessionPlanSection({ session, teamName }: { session: PlanSession; teamName?: string | null }): JSX.Element {
  const { can } = useAuth();
  const canEdit = can(TRAINING_PLANS_RESOURCE, 'update');
  const queryClient = useQueryClient();
  const toast = useToast();

  const [panel, setPanel] = useState<Panel>('plan');
  const [editor, setEditor] = useState<PlanDraft | null>(null);
  const [printRun, setPrintRun] = useState(0);
  const [templateTitle, setTemplateTitle] = useState('');

  const planKey = ['training-session-plan', session.id];
  const planQuery = useQuery({
    queryKey: planKey,
    queryFn: () => api.get<TrainingPlanDto | null>(`/api/training/sessions/${session.id}/plan`),
  });
  const plan = planQuery.data ?? null;

  const templates = useQuery({
    queryKey: ['training-templates'],
    queryFn: () => api.get<TrainingPlanSummaryDto[]>('/api/training/plans/templates'),
    enabled: panel === 'templates',
  });

  const fail = (fallback: string) => (error: unknown) => toast.show(error instanceof ApiError ? error.message : fallback, 'error');
  const setPlan = (value: TrainingPlanDto | null): void => {
    queryClient.setQueryData(planKey, value);
  };

  const save = useMutation({
    mutationFn: (draft: PlanDraft) => api.put<TrainingPlanDto>(`/api/training/sessions/${session.id}/plan`, draftToInput(draft)),
    onSuccess: (saved) => {
      setPlan(saved);
      setEditor(null);
      toast.show('Einheit gespeichert');
    },
    // Der Editor bleibt offen, damit nichts verloren geht; die Meldung steht auch im Editor.
    onError: fail('Speichern fehlgeschlagen'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/training/sessions/${session.id}/plan`),
    onSuccess: () => {
      setPlan(null);
      toast.show('Einheit gelöscht');
    },
    onError: fail('Die Einheit konnte nicht gelöscht werden'),
  });

  const fromTemplate = useMutation({
    mutationFn: (planId: string) => api.post<TrainingPlanDto>(`/api/training/sessions/${session.id}/plan/from-template/${planId}`),
    onSuccess: (created) => {
      setPlan(created);
      setPanel('plan');
      toast.show('Vorlage übernommen');
    },
    onError: fail('Die Vorlage konnte nicht übernommen werden'),
  });

  const saveAsTemplate = useMutation({
    mutationFn: () => api.post<TrainingPlanDto>(`/api/training/sessions/${session.id}/plan/save-as-template`, { title: templateTitle.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['training-templates'] });
      setPanel('plan');
      toast.show('Als Vorlage gespeichert');
    },
    onError: fail('Die Vorlage konnte nicht gespeichert werden'),
  });

  /** Druckt, sobald das Blatt vollstaendig geladen ist; danach raeumt "afterprint" auf. */
  const printNow = useCallback(() => {
    const done = (): void => {
      document.body.classList.remove(PRINT_BODY_CLASS);
      window.removeEventListener('afterprint', done);
      setPrintRun(0);
    };
    window.addEventListener('afterprint', done);
    document.body.classList.add(PRINT_BODY_CLASS);
    window.print();
  }, []);

  const pickTemplate = (template: TrainingPlanSummaryDto): void => {
    if (plan && !window.confirm('Die vorhandene Einheit wird durch die Vorlage ersetzt. Fortfahren?')) return;
    fromTemplate.mutate(template.id);
  };

  const openEditor = (): void => {
    save.reset();
    setEditor(plan ? draftFromPlan(plan) : emptyDraft());
  };

  return (
    <section aria-label="Einheit" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-slate-900">Einheit</h3>
          <TestBadge />
        </div>
        {plan && <p className="min-w-0 break-words text-sm text-slate-600">{plan.title}</p>}
      </div>

      {planQuery.isLoading && <LoadingBlock />}
      {planQuery.error && <ErrorBlock error={planQuery.error} />}

      {panel === 'templates' && (
        <div className="space-y-3 rounded-xl border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-semibold text-slate-900">Vorlage wählen</h4>
            <Button variant="ghost" size="sm" onClick={() => setPanel('plan')}>Zurück</Button>
          </div>
          {templates.isLoading && <LoadingBlock />}
          {templates.error && <ErrorBlock error={templates.error} />}
          {templates.data && templates.data.length === 0 && (
            <EmptyState title="Noch keine Vorlagen" description="Speichere eine Einheit „Als Vorlage“ oder lege unter „Einheiten“ eine an." />
          )}
          <ul className="divide-y divide-slate-100">
            {templates.data?.map((template) => (
              <li key={template.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <span className="min-w-0">
                  <span className="block break-words font-medium text-slate-900">{template.title}</span>
                  <span className="block text-xs text-slate-500">
                    {template.itemCount} {template.itemCount === 1 ? 'Eintrag' : 'Einträge'} · {template.totalMinutes} Min.
                    {template.focus && ` · ${template.focus}`}
                    {template.teamName && ` · ${template.teamName}`}
                  </span>
                </span>
                <Button size="sm" variant="secondary" loading={fromTemplate.isPending && fromTemplate.variables === template.id} onClick={() => pickTemplate(template)}>
                  Übernehmen<span className="sr-only"> · {template.title}</span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {panel === 'saveTemplate' && (
        <div className="space-y-3 rounded-xl border border-slate-200 p-3">
          <h4 className="font-semibold text-slate-900">Als Vorlage speichern</h4>
          <Field label="Name der Vorlage" required hint="Die Vorlage ist für alle Trainer des Vereins sichtbar.">
            {(id) => <Input id={id} value={templateTitle} maxLength={150} onChange={(event) => setTemplateTitle(event.target.value)} autoFocus />}
          </Field>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => setPanel('plan')}>Abbrechen</Button>
            <Button loading={saveAsTemplate.isPending} disabled={!templateTitle.trim()} onClick={() => saveAsTemplate.mutate()}>Vorlage speichern</Button>
          </div>
        </div>
      )}

      {panel === 'plan' && !planQuery.isLoading && !planQuery.error && (
        plan ? (
          <>
            <div className="flex flex-wrap gap-2">
              {canEdit && <Button variant="secondary" size="sm" onClick={openEditor}>Bearbeiten</Button>}
              {canEdit && <Button variant="secondary" size="sm" onClick={() => setPanel('templates')}>Aus Vorlage übernehmen</Button>}
              {canEdit && (
                <Button variant="secondary" size="sm" onClick={() => { setTemplateTitle(plan.title || session.title); setPanel('saveTemplate'); }}>
                  Als Vorlage speichern
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => setPrintRun((run) => run + 1)}>Drucken</Button>
              <PlanImageButton plan={plan} subtitle={printSubtitle(session, teamName)} />
              <OfflineSaveButton
                plan={plan}
                meta={{ kind: 'session', sessionId: session.id, title: session.title, startAt: session.start_at, endAt: session.end_at, location: session.location, teamName }}
              />
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                  loading={remove.isPending}
                  onClick={() => { if (window.confirm('Die Einheit dieses Trainings löschen?')) remove.mutate(); }}
                >
                  Löschen
                </Button>
              )}
            </div>
            <PlanView plan={plan} />
          </>
        ) : (
          <EmptyState
            title="Noch keine Einheit geplant"
            description={canEdit ? 'Stelle die Einheit aus Übungen zusammen oder übernimm eine Vorlage.' : undefined}
            action={canEdit ? (
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={openEditor}>Einheit planen</Button>
                <Button variant="secondary" onClick={() => setPanel('templates')}>Aus Vorlage übernehmen</Button>
              </div>
            ) : undefined}
          />
        )
      )}

      {editor && (
        <PlanEditor
          title={plan ? 'Einheit bearbeiten' : 'Einheit planen'}
          mode="session"
          initial={editor}
          plannedMinutes={sessionMinutes(session.start_at, session.end_at)}
          saving={save.isPending}
          serverError={save.error ? (save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen') : null}
          onSave={(draft) => save.mutate(draft)}
          onClose={() => setEditor(null)}
        />
      )}

      {printRun > 0 && plan && <PlanPrintSheet key={printRun} plan={plan} subtitle={printSubtitle(session, teamName)} onReady={printNow} />}
    </section>
  );
}

/**
 * Knopf "Einheit" in der Terminzeile eines Trainings. Der Abschnitt oeffnet
 * sich in einem Dialog; die Abfrage startet erst dann.
 */
export function SessionPlanButton({ session, teamName }: { session: PlanSession; teamName?: string | null }): JSX.Element | null {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  if (!can(TRAINING_PLANS_RESOURCE, 'read')) return null;
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)} aria-label={`Einheit · ${session.title}`}>
        Einheit
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={`Einheit · ${session.title}`} wide footer={<Button variant="secondary" onClick={() => setOpen(false)}>Schließen</Button>}>
        <SessionPlanSection session={session} teamName={teamName} />
      </Dialog>
    </>
  );
}
